import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, Calendar, User, Mail, CheckCircle, XCircle, 
  MessageSquare, Eye, Clock, Hash, Tag, ChevronDown, Download,
  RefreshCw, SortAsc, SortDesc, Grid, List, Users, Image,
  AlertTriangle, Star, ThumbsUp, ThumbsDown, Loader2, X, ZoomIn, ZoomOut, Move
} from 'lucide-react';
import { Query, Models } from 'appwrite';
import { databases } from '../../services/appwriteService';

const appwriteDatabaseId = process.env.REACT_APP_APPWRITE_DATABASE_ID || 'focal-db';

const cleanImageUrl = (url: string): string => {
    if (!url) return '';
    
    // Si l'URL contient &transformation_matrix, on supprime tout à partir de là
    if (url.includes('&transformation_matrix')) {
      return url.split('&transformation_matrix')[0];
    }
    
    // Si l'URL contient d'autres paramètres problématiques après le token
    if (url.includes('&dlbox=')) {
      return url.split('&dlbox=')[0];
    }
    
    if (url.includes('&poly=')) {
      return url.split('&poly=')[0];
    }
    
    return url;
  };

// ✅ Types avec issue_names ajouté
interface AuditResult extends Models.Document {
  match_task_id: number;
  agent_email: string;
  task_date: string;
  audited_at: string;
  auditor_id: string;  // ✅ AJOUTER auditor_id
  auditor_email?: string;  // ✅ AJOUTER auditor_email
  auditor_name?: string;  // Nom enrichi
  audit_status: 'approved' | 'rejected';
  audit_comment?: string;
  task_type: 'pair_only' | 'complete_match';
  agent_submitted_url?: string;
  model_template_url?: string;
  processing_time_seconds: number;
  issue_names?: string;
  template_image_url?: string;
}

interface Auditor extends Models.Document {
  full_name: string;
  email: string;
  role: string;
}

interface RawAuditDocument extends Models.Document {
  match_task_id: number;
  agent_email: string;
  task_date: string;
  audited_at: string;
  auditor_id: string;  // ✅ CHANGER de audited_by vers auditor_id
  auditor_email: string;  // ✅ AJOUTER ce champ
  audit_status: 'approved' | 'rejected';
  audit_comment?: string;
  task_type: 'pair_only' | 'complete_match';
  agent_submitted_url?: string;
  model_template_url?: string;
  processing_time_seconds: number;
  issue_names?: string;
  template_image_url?: string;
}

const ImageWithFallback: React.FC<{
    src: string;
    fallbackSrc?: string;
    alt: string;
    className: string;
    onClick?: () => void;
  }> = ({ src, fallbackSrc, alt, className, onClick }) => {
    const [imageError, setImageError] = useState(false);
    const [currentSrc, setCurrentSrc] = useState(cleanImageUrl(src));
    const [isLoading, setIsLoading] = useState(true);
  
    useEffect(() => {
        setImageError(false);
        setCurrentSrc(cleanImageUrl(src));
        setIsLoading(true);
      }, [src]);
  
    const handleError = () => {
        if (!imageError && fallbackSrc && currentSrc !== cleanImageUrl(fallbackSrc)) {
            setCurrentSrc(cleanImageUrl(fallbackSrc));
        setImageError(false);
      } else {
        setImageError(true);
        setIsLoading(false);
      }
    };
  
    const handleLoad = () => {
      setIsLoading(false);
    };
  
    if (imageError) {
      return (
        <div 
          className={`bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center ${className}`}
          onClick={onClick}
        >
          <div className="text-center text-gray-500">
            <Image className="w-8 h-8 mx-auto mb-2" />
            <p className="text-xs">Image unavailable</p>
            <p className="text-xs text-gray-400 mt-1">Click to try reload</p>
          </div>
        </div>
      );
    }
  
    return (
      <div className="relative">
        {isLoading && (
          <div className={`absolute inset-0 bg-gray-100 flex items-center justify-center ${className}`}>
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        )}
        <img
          src={currentSrc}
          alt={alt}
          className={className}
          onError={handleError}
          onLoad={handleLoad}
          onClick={onClick}
          style={{ display: isLoading ? 'none' : 'block' }}
        />
      </div>
    );
  };
  
  // Composant ZoomableImage REMPLACÉ
  const ZoomableImage: React.FC<{
    src: string;
    alt: string;
    className?: string;
    fallbackSrc?: string;
  }> = ({ src, alt, className, fallbackSrc }) => {
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
    const handleZoomIn = () => setScale(prev => Math.min(prev * 1.5, 5));
    const handleZoomOut = () => setScale(prev => Math.max(prev / 1.5, 1));
    const handleReset = () => {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    };
  
    const handleMouseDown = (e: React.MouseEvent) => {
      if (scale > 1) {
        setIsDragging(true);
        setDragStart({
          x: e.clientX - position.x,
          y: e.clientY - position.y
        });
      }
    };
  
    const handleMouseMove = (e: React.MouseEvent) => {
      if (isDragging && scale > 1) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        });
      }
    };
  
    const handleMouseUp = () => {
      setIsDragging(false);
    };
  
    return (
      <div className="relative group">
        <div className="overflow-hidden rounded-2xl bg-gray-100 relative">
          <div
            style={{
              transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
              transformOrigin: 'center center',
              transition: isDragging ? 'none' : 'transform 0.2s ease'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            className={isDragging ? 'cursor-grabbing' : scale > 1 ? 'cursor-grab' : 'cursor-zoom-in'}
          >
            <ImageWithFallback
              src={src}
              fallbackSrc={fallbackSrc}
              alt={alt}
              className={`transition-transform duration-200 ${className}`}
              onClick={scale === 1 ? handleZoomIn : undefined}
            />
          </div>
        </div>
        
        {/* Zoom Controls */}
        <div className="absolute bottom-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={handleZoomIn}
            className="bg-black/70 text-white p-2 rounded-lg hover:bg-black/80 transition-colors"
            disabled={scale >= 5}
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            onClick={handleZoomOut}
            className="bg-black/70 text-white p-2 rounded-lg hover:bg-black/80 transition-colors"
            disabled={scale <= 1}
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <button
            onClick={handleReset}
            className="bg-black/70 text-white p-2 rounded-lg hover:bg-black/80 transition-colors"
          >
            <Move className="w-4 h-4" />
          </button>
        </div>
        
        {scale > 1 && (
          <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded-lg text-sm">
            {Math.round(scale * 100)}%
          </div>
        )}
      </div>
    );
  };
  


// ✅ Composant Modal avec focus sur les commentaires
const AuditModal: React.FC<{
  result: AuditResult | null;
  onClose: () => void;
}> = ({ result, onClose }) => {
  if (!result) return null;

  const imageUrl = result.task_type === 'complete_match' 
  ? result.template_image_url 
  : (result.agent_submitted_url || result.model_template_url);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-auto">
        {/* Header avec Status proéminent */}
        <div className={`sticky top-0 ${result.audit_status === 'approved' ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-gradient-to-r from-red-600 to-red-700'} text-white p-6 rounded-t-3xl`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                {result.audit_status === 'approved' ? (
                  <CheckCircle className="w-8 h-8" />
                ) : (
                  <XCircle className="w-8 h-8" />
                )}
                <h2 className="text-2xl font-bold">
                  Task #{result.match_task_id} - {result.audit_status.toUpperCase()}
                </h2>
              </div>
              <p className="text-white/90 mt-1">Quality audit detailed review</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-8">
          {/* ✅ COMMENTS EN PREMIER - Mise en avant */}
          {result.audit_comment && result.audit_comment.trim() !== '' ? (
            <div className="mb-8">
              <div className={`${result.audit_status === 'rejected' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'} border-2 rounded-2xl p-6`}>
                <div className="flex items-start gap-3">
                  <MessageSquare className={`w-6 h-6 ${result.audit_status === 'rejected' ? 'text-red-600' : 'text-yellow-600'} mt-1 flex-shrink-0`} />
                  <div className="flex-1">
                    <h3 className={`text-xl font-bold ${result.audit_status === 'rejected' ? 'text-red-800' : 'text-yellow-800'} mb-3`}>
                      Audit Feedback
                    </h3>
                    <div className={`${result.audit_status === 'rejected' ? 'bg-red-100 border-red-300' : 'bg-yellow-100 border-yellow-300'} border rounded-xl p-4`}>
                      <p className={`${result.audit_status === 'rejected' ? 'text-red-800' : 'text-yellow-800'} text-lg leading-relaxed font-medium`}>
                        {result.audit_comment}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-8">
              <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <h3 className="text-xl font-bold text-green-800">No Issues Found</h3>
                    <p className="text-green-700 mt-1">This task passed audit without any comments</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Image Section */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <ZoomIn className="w-5 h-5" />
                Task Image (Click to zoom)
              </h3>
              {imageUrl ? (
            <ZoomableImage
            src={imageUrl}
            alt="Task Image"
            className="w-full h-96 object-cover"
            fallbackSrc={
                result.task_type === 'complete_match' 
                  ? undefined // Pas de fallback pour complete_match, juste template_image_url
                  : (result.model_template_url !== imageUrl ? result.model_template_url : result.agent_submitted_url)
              }
          />
            ) : (
            <div className="w-full h-96 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center">
                <div className="text-center text-gray-500">
                <Image className="w-12 h-12 mx-auto mb-2" />
                <p>No image available</p>
                </div>
            </div>
            )}
            </div>

            {/* Details Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-800">Task Details</h3>
              
              {/* Info Grid */}
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Hash className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium text-gray-500">Task ID</span>
                  </div>
                  <p className="text-lg font-bold text-gray-800">#{result.match_task_id}</p>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium text-gray-500">Agent Email</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-800">{result.agent_email}</p>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-medium text-gray-500">Auditor</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-800">{result.auditor_name || 'Unknown Auditor'}</p>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium text-gray-500">Task Type</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-800">{result.task_type.replace('_', ' ').toUpperCase()}</p>
                </div>

                {/* ✅ Issue Names */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    <span className="text-sm font-medium text-gray-500">Issue Labels</span>
                  </div>
                  {result.issue_names && result.issue_names.trim() ? (
                    <p className="text-lg font-semibold text-gray-800">{result.issue_names}</p>
                  ) : (
                    <p className="text-lg text-gray-500 italic">No issue labels</p>
                  )}
                </div>
              </div>

              {/* Dates */}
              <div className="bg-blue-50 rounded-xl p-4">
                <h4 className="font-semibold text-blue-800 mb-3">Timeline</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="text-blue-700">Task Date: {new Date(result.task_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-blue-600" />
                    <span className="text-blue-700">Audited: {new Date(result.audited_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AuditResultsInterface: React.FC = () => {
  const [auditResults, setAuditResults] = useState<AuditResult[]>([]);
  const [filteredResults, setFilteredResults] = useState<AuditResult[]>([]);
  const [auditors, setAuditors] = useState<Auditor[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedResult, setSelectedResult] = useState<AuditResult | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
const [lastDocumentId, setLastDocumentId] = useState<string | null>(null);
const [totalCount, setTotalCount] = useState(0);
  
  // Dates par défaut: premier jour du mois courant jusqu'à aujourd'hui
  const getDefaultDates = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      dateFrom: firstDayOfMonth.toISOString().split('T')[0],
      dateTo: today.toISOString().split('T')[0]
    };
  };

  const [filters, setFilters] = useState({
    ...getDefaultDates(),
    auditor: '',
    agentEmail: '',
    taskType: '',
    auditStatus: 'rejected',
    dateType: 'month'
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('audited_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);

  const [globalStats, setGlobalStats] = useState({
    total: 0,
    approved: 0,
    rejected: 0,
    withComments: 0
  });
  
  const [displayStats, setDisplayStats] = useState({
    displayed: 0,
    displayedApproved: 0,
    displayedRejected: 0,
    displayedWithComments: 0
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    rejected: 0,
    withComments: 0
  });

  const getTotalCount = async () => {
    try {
      const countQueries = [
        Query.equal('audit_status', ['approved', 'rejected'])
      ];
  
      // ✅ AJOUTER LES MÊMES FILTRES pour le comptage
      if (filters.dateFrom) {
        countQueries.push(Query.greaterThanEqual('audited_at', `${filters.dateFrom}T00:00:00.000Z`));
      }
      if (filters.dateTo) {
        countQueries.push(Query.lessThanEqual('audited_at', `${filters.dateTo}T23:59:59.999Z`));
      }
      if (filters.auditor) {
        countQueries.push(Query.equal('audited_by', filters.auditor));
      }
      if (filters.taskType) {
        countQueries.push(Query.equal('task_type', filters.taskType));
      }
      if (filters.auditStatus) {
        countQueries.push(Query.equal('audit_status', filters.auditStatus));
      }
  
      const countData = await databases.listDocuments(
        appwriteDatabaseId,
        'audit_tasks',
        [
          ...countQueries,
          Query.select(['$id']),
          Query.limit(5000) // Limite plus haute pour le comptage
        ]
      );
  
      setTotalCount(countData.total || countData.documents.length);
      console.log('📊 Total avec filtres:', countData.total || countData.documents.length);
    } catch (error) {
      console.error('Error getting total count:', error);
    }
  };

  const calculateGlobalStats = async () => {
    try {
      console.log('📊 Calcul des statistiques GLOBALES RÉELLES...');
      
      // ✅ STATS GLOBALES = TOUS LES AUDITS (indépendamment des filtres d'affichage)
      const globalBaseQueries = [
        Query.equal('audit_status', ['approved', 'rejected']),
        Query.isNotNull('auditor_id')
      ];
      
      // ✅ APPLIQUER SEULEMENT les filtres de date et auditeur pour les stats globales
      // PAS le filtre auditStatus qui est juste pour l'affichage
      if (filters.dateFrom) {
        globalBaseQueries.push(Query.greaterThanEqual('audited_at', `${filters.dateFrom}T00:00:00.000Z`));
      }
      if (filters.dateTo) {
        globalBaseQueries.push(Query.lessThanEqual('audited_at', `${filters.dateTo}T23:59:59.999Z`));
      }
      if (filters.auditor) {
        globalBaseQueries.push(Query.equal('auditor_id', filters.auditor));
      }
      if (filters.taskType) {
        globalBaseQueries.push(Query.equal('task_type', filters.taskType));
      }
      
      // ✅ IMPORTANT: Ne PAS appliquer filters.auditStatus ici
      // Car on veut le TOTAL réel (approved + rejected) dans les stats
  
      // Fonction de comptage par batch (même que avant)
      const countByBatches = async (queries: any[]) => {
        let totalCount = 0;
        let offset = 0;
        const batchSize = 5000;
        let hasMore = true;
  
        while (hasMore) {
          try {
            const batchQuery = await databases.listDocuments(
              appwriteDatabaseId,
              'audit_tasks',
              [
                ...queries,
                Query.select(['$id']),
                Query.limit(batchSize),
                Query.offset(offset)
              ]
            );
  
            const batchCount = batchQuery.documents.length;
            totalCount += batchCount;
            
            console.log(`📊 Batch ${Math.floor(offset/batchSize) + 1}: ${batchCount} documents (total: ${totalCount})`);
            
            if (batchCount < batchSize) {
              hasMore = false;
            } else {
              offset += batchSize;
            }
            
            if (offset > 100000) {
              console.warn('⚠️ Limite de sécurité atteinte à 100k documents');
              hasMore = false;
            }
            
          } catch (error) {
            console.error(`❌ Erreur batch ${Math.floor(offset/batchSize) + 1}:`, error);
            hasMore = false;
          }
        }
  
        return totalCount;
      };
  
      // ✅ Comptage séparé pour chaque statut (BASÉ SUR LES DONNÉES RÉELLES)
      const [totalCount, approvedCount, rejectedCount] = await Promise.all([
        countByBatches(globalBaseQueries), // TOUS les audits
        countByBatches([...globalBaseQueries, Query.equal('audit_status', 'approved')]), // TOUS les approved
        countByBatches([...globalBaseQueries, Query.equal('audit_status', 'rejected')]) // TOUS les rejected
      ]);
  
      // Comptage des commentaires (même logique)
      let withCommentsCount = 0;
      let commentsOffset = 0;
      const commentsBatchSize = 2000;
      let hasMoreComments = true;
  
      while (hasMoreComments) {
        try {
          const commentsQuery = await databases.listDocuments(
            appwriteDatabaseId,
            'audit_tasks',
            [
              ...globalBaseQueries, // Utiliser les requêtes globales
              Query.select(['$id', 'audit_comment']),
              Query.limit(commentsBatchSize),
              Query.offset(commentsOffset)
            ]
          );
  
          const batchWithComments = commentsQuery.documents.filter(
            (doc: any) => doc.audit_comment && doc.audit_comment.trim() !== ''
          ).length;
  
          withCommentsCount += batchWithComments;
          
          if (commentsQuery.documents.length < commentsBatchSize) {
            hasMoreComments = false;
          } else {
            commentsOffset += commentsBatchSize;
          }
  
          if (commentsOffset > 50000) {
            console.warn('⚠️ Limite commentaires atteinte à 50k');
            hasMoreComments = false;
          }
  
        } catch (error) {
          console.error('❌ Erreur batch commentaires:', error);
          hasMoreComments = false;
        }
      }
  
      const newGlobalStats = {
        total: totalCount,        // ✅ TOTAL RÉEL (approved + rejected)
        approved: approvedCount,  // ✅ TOUS les approved
        rejected: rejectedCount,  // ✅ TOUS les rejected  
        withComments: withCommentsCount // ✅ TOUS avec commentaires
      };
  
      console.log('📊 STATISTIQUES GLOBALES RÉELLES (indépendantes des filtres d\'affichage):', newGlobalStats);
      setGlobalStats(newGlobalStats);
  
    } catch (error) {
      console.error('❌ Erreur calcul statistiques globales:', error);
    }
  };
  
  
  // ✅ NOUVELLE FONCTION: Calculer les stats des résultats affichés seulement
  const calculateDisplayStats = (results: AuditResult[]) => {
    const displayStatsData = {
      displayed: results.length,
      displayedApproved: results.filter(r => r.audit_status === 'approved').length,
      displayedRejected: results.filter(r => r.audit_status === 'rejected').length,
      displayedWithComments: results.filter(r => r.audit_comment && r.audit_comment.trim() !== '').length
    };
    
    console.log('📊 Stats affichées:', displayStatsData);
    setDisplayStats(displayStatsData);
  };

  // ✅ Fonction pour actualiser toutes les données
  const handleRefreshData = async () => {
    if (filterLoading) return;
    
    console.log('🔄 Actualisation complète des données...');
    setLastDocumentId(null);
    setHasMore(true);
    
    try {
      // ✅ D'abord recalculer les stats globales
      await calculateGlobalStats();
      // ✅ Ensuite recharger les résultats
      await loadAuditResults(false);
      
      console.log('✅ Actualisation terminée');
    } catch (error) {
      console.error('❌ Erreur lors de l\'actualisation:', error);
    }
  };

useEffect(() => {
  if (auditors.length > 0) {
    // ✅ D'abord calculer les stats globales
    calculateGlobalStats().then(() => {
      // ✅ Ensuite charger les résultats paginés
      loadAuditResults(false);
    });
  }
}, [auditors, filters.dateFrom, filters.dateTo, filters.auditor, filters.taskType, filters.auditStatus]);

  // ✅ Charger les auditeurs en premier
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        await loadAuditors();
      } catch (error) {
        console.error('Error loading auditors:', error);
      }
    };
    
    initializeData();
  }, []);

  // ✅ Charger les résultats après que les auditeurs soient chargés
  useEffect(() => {
    if (auditors.length > 0) {
      loadAuditResults();
    }
  }, [auditors, filters.dateFrom, filters.dateTo]);

  const loadAuditors = async () => {
    try {
      console.log('📋 Chargement des auditeurs...');
      
      const auditorsData = await databases.listDocuments(
        appwriteDatabaseId,
        'users',
        [
          Query.select(['$id', 'full_name', 'email', 'role']),
          Query.equal('role', 'auditor'),
          Query.limit(200) // ✅ Augmenté de 100 à 200
        ]
      );
      
      console.log('✅ Auditeurs chargés:', auditorsData.documents.length);
      console.log('📋 Premiers auditeurs:', auditorsData.documents.slice(0, 3).map(a => ({ id: a.$id, name: a.full_name })));
      
      setAuditors(auditorsData.documents as unknown as Auditor[]);
    } catch (error) {
      console.error('❌ Error loading auditors:', error);
      setAuditors([]);
    }
  };

  const calculateStats = (results: AuditResult[]) => {
  const statsData = {
    total: totalCount,
    approved: results.filter(r => r.audit_status === 'approved').length,
    rejected: results.filter(r => r.audit_status === 'rejected').length,
    withComments: results.filter(r => r.audit_comment && r.audit_comment.trim() !== '').length
  };
  setStats(statsData);
};

const loadAuditResults = async (loadMore: boolean = false) => {
  setFilterLoading(true);
  try {
    const queries = [
      Query.equal('audit_status', ['approved', 'rejected']),
      Query.orderDesc('audited_at'),
      Query.isNotNull('auditor_id')  // ✅ CHANGER vers auditor_id
    ];

    // Filtres
    if (filters.dateFrom) {
      queries.push(Query.greaterThanEqual('audited_at', `${filters.dateFrom}T00:00:00.000Z`));
    }
    if (filters.dateTo) {
      queries.push(Query.lessThanEqual('audited_at', `${filters.dateTo}T23:59:59.999Z`));
    }
    if (filters.auditor) {
      queries.push(Query.equal('auditor_id', filters.auditor));  // ✅ CHANGER vers auditor_id
    }
    if (filters.taskType) {
      queries.push(Query.equal('task_type', filters.taskType));
    }
    if (filters.auditStatus) {
      queries.push(Query.equal('audit_status', filters.auditStatus));
    }

    // Limites augmentées
    if (loadMore && lastDocumentId) {
      queries.push(Query.cursorAfter(lastDocumentId));
      queries.push(Query.limit(500));
    } else {
      const estimatedLimit = filters.auditor || filters.taskType || filters.auditStatus ? 2000 : 1000;
      queries.push(Query.limit(estimatedLimit));
    }

    console.log('📊 Requête avec auditor_id:', queries);

    const auditData = await databases.listDocuments(
      appwriteDatabaseId,
      'audit_tasks',
      queries
    );

    console.log('📊 Résultats chargés:', auditData.documents.length);

    // ✅ ENRICHISSEMENT CORRIGÉ avec auditor_id
    const enrichedResults = auditData.documents.map((result: Models.Document, index: number) => {
      const rawResult = result as unknown as RawAuditDocument;
      
      // ✅ Debug avec auditor_id
      if (index < 3) {
        console.log(`Debug tâche ${index}:`, {
          match_task_id: rawResult.match_task_id,
          auditor_id: rawResult.auditor_id,
          auditor_email: rawResult.auditor_email
        });
      }
      
      // ✅ Recherche par auditor_id
      const auditor = auditors.find(a => a.$id === rawResult.auditor_id);
      
      if (!auditor && rawResult.auditor_id) {
        console.warn(`⚠️ Auditeur ID ${rawResult.auditor_id} non trouvé:`, {
          recherche: rawResult.auditor_id,
          email_dans_task: rawResult.auditor_email,
          auditeurs_disponibles: auditors.slice(0, 3).map(a => ({ id: a.$id, name: a.full_name }))
        });
      }
      
      return {
        ...rawResult,
        // ✅ AMÉLIORER le fallback avec auditor_email
        auditor_name: auditor?.full_name || 
                     auditor?.email || 
                     rawResult.auditor_email ||  // ✅ UTILISER auditor_email comme fallback
                     (rawResult.auditor_id ? `Auditor ${rawResult.auditor_id.slice(-6)}` : 'System Audit')
      } as AuditResult;
    });

    // Gestion de la pagination
    let finalResults: AuditResult[];
    if (loadMore) {
      finalResults = [...auditResults, ...enrichedResults];
      setAuditResults(finalResults);
    } else {
      finalResults = enrichedResults;
      setAuditResults(enrichedResults);
    }

    // ✅ Pagination mise à jour
    const limitUsed = loadMore ? 500 : (filters.auditor || filters.taskType || filters.auditStatus ? 2000 : 1000);
    setHasMore(auditData.documents.length === limitUsed);
    setLastDocumentId(auditData.documents.length > 0 ? auditData.documents[auditData.documents.length - 1].$id : null);
    
    // ✅ IMPORTANT: Ne pas recalculer les stats globales ici pour les résultats paginés
    if (!loadMore) {
      // Seulement au premier chargement, on recalcule les stats spécifiques aux résultats affichés
      calculateDisplayStats(finalResults);
    }
    
    setLoading(false);
    
  } catch (error) {
    console.error('Error loading audit results:', error);
    setAuthError('Unable to load audit results. Please check database permissions.');
    setLoading(false);
  } finally {
    setFilterLoading(false);
  }
};
  
  // ✅ Ajouter une fonction pour charger plus
  const loadMoreResults = () => {
    if (hasMore && !filterLoading) {
      loadAuditResults(true);
    }
  };
  
  // ✅ Modifier le bouton "Load More"
  {filteredResults.length > 0 && hasMore && (
    <div className="text-center mt-12">
      <button 
        onClick={loadMoreResults}
        disabled={filterLoading}
        className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50"
      >
        {filterLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading more...
          </>
        ) : (
          <>
            <RefreshCw className="w-5 h-5" />
            Load More Results
          </>
        )}
      </button>
      <p className="text-sm text-gray-500 mt-2">
        Showing {filteredResults.length} results
      </p>
    </div>
  )}

  // Appliquer les filtres et le tri
  useEffect(() => {
    setFilterLoading(true);
    
    const timeoutId = setTimeout(() => {
      let filtered = [...auditResults];
      
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filtered = filtered.filter(result => 
          result.agent_email.toLowerCase().includes(searchLower) ||
          result.match_task_id.toString().includes(searchTerm) ||
          result.auditor_name?.toLowerCase().includes(searchLower) ||
          result.audit_comment?.toLowerCase().includes(searchLower) ||
          result.issue_names?.toLowerCase().includes(searchLower)
        );
      }
      
      // Filtre par email agent (côté client car c'est une recherche partielle)
      if (filters.agentEmail) {
        filtered = filtered.filter(result => 
          result.agent_email.toLowerCase().includes(filters.agentEmail.toLowerCase())
        );
      }
      
      filtered.sort((a, b) => {
        let aValue: any = a[sortBy as keyof AuditResult];
        let bValue: any = b[sortBy as keyof AuditResult];
        
        if (aValue === undefined) aValue = '';
        if (bValue === undefined) bValue = '';
        
        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();
        
        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
      
      setFilteredResults(filtered);
      calculateStats(filtered);
      setFilterLoading(false);
    }, 300);

    return () => clearTimeout(timeoutId);
    
  }, [auditResults, searchTerm, filters, sortBy, sortOrder]);
  useEffect(() => {
    const handleScroll = () => {
      const stickyFilters = document.getElementById('sticky-filters');
      if (stickyFilters) {
        if (window.scrollY > 400) {
          stickyFilters.style.transform = 'translateY(0)';
        } else {
          stickyFilters.style.transform = 'translateY(-100%)';
        }
      }
    };
  
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const handleDateTypeChange = (type: string) => {
    const today = new Date();
    let dateFrom = today.toISOString().split('T')[0];
    let dateTo = today.toISOString().split('T')[0];
    
    switch (type) {
      case 'today':
        // Already set above
        break;
      case 'week':
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        dateFrom = weekAgo.toISOString().split('T')[0];
        break;
      case 'month':
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        dateFrom = firstDayOfMonth.toISOString().split('T')[0];
        break;
      case 'custom':
        // ✅ Pour custom, on garde les dates actuelles et on ne fait que changer le type
        setFilters(prev => ({ ...prev, dateType: type }));
        return; // ✅ IMPORTANT : return ici pour ne pas écraser les dates
    }
    
    setFilters(prev => ({ ...prev, dateType: type, dateFrom, dateTo }));
  };

  const exportData = () => {
    const csvContent = [
      ['Task ID', 'Agent Email', 'Auditor', 'Status', 'Task Date', 'Audit Date', 'Type', 'Issue Names', 'Comment'].join(','),
      ...filteredResults.map(result => [
        result.match_task_id,
        result.agent_email,
        result.auditor_name || '',
        result.audit_status,
        formatDate(result.task_date),
        formatDate(result.audited_at),
        result.task_type,
        result.issue_names || '',
        `"${result.audit_comment || 'No comment'}"`
      ].join(','))
    ].join('\\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-results-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ✅ Affichage d'erreur si problème de connexion
  if (authError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-red-100">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Connection Error</h2>
            <p className="text-gray-600 mb-6">{authError}</p>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-blue-600 text-white px-6 py-2 rounded-xl hover:bg-blue-700 transition-colors font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-8">
            <div className="absolute inset-0 rounded-full border-4 border-gray-200"></div>
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 border-r-purple-600 animate-spin"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Audit Results</h2>
          <p className="text-gray-600">Fetching data from database...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        {/* Header */}
        <div className="bg-white shadow-xl border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Quality Audit Dashboard
                </h1>
                <p className="text-lg text-gray-600 mt-2">Professional audit results and quality assurance metrics</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-2 rounded-xl text-sm font-medium">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                 Public Dashboard
               </div>
               <button 
                 onClick={exportData}
                 className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
               >
                 <Download className="w-5 h-5" />
                 Export Data
               </button>
               <button 
                 onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                 className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all"
               >
                 {viewMode === 'grid' ? <List className="w-5 h-5" /> : <Grid className="w-5 h-5" />}
                 {viewMode === 'grid' ? 'List View' : 'Grid View'}
               </button>
             </div>
           </div>

           {/* Stats Cards */}
           <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
  <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white relative overflow-hidden">
    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
    <div className="relative z-10">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-blue-100 text-sm font-medium">Total Audited</p>
          <p className="text-3xl font-bold">{filterLoading ? '...' : globalStats.total.toLocaleString()}</p>
          <p className="text-blue-200 text-xs mt-1">
            Showing {displayStats.displayed.toLocaleString()} in current view
          </p>
        </div>
        <Eye className="w-8 h-8 text-blue-200" />
      </div>
    </div>
  </div>
  
  <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 text-white relative overflow-hidden">
    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
    <div className="relative z-10">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-green-100 text-sm font-medium">Approved</p>
          <p className="text-3xl font-bold">{filterLoading ? '...' : globalStats.approved.toLocaleString()}</p>
          <p className="text-green-200 text-xs mt-1">
            {globalStats.total > 0 ? Math.round((globalStats.approved / globalStats.total) * 100) : 0}% of total
          </p>
        </div>
        <ThumbsUp className="w-8 h-8 text-green-200" />
      </div>
    </div>
  </div>
  
  <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 text-white relative overflow-hidden">
    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
    <div className="relative z-10">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-orange-100 text-sm font-medium">With Comments</p>
          <p className="text-3xl font-bold">{filterLoading ? '...' : globalStats.withComments.toLocaleString()}</p>
          <p className="text-orange-200 text-xs mt-1">
            {globalStats.total > 0 ? Math.round((globalStats.withComments / globalStats.total) * 100) : 0}% have feedback
          </p>
        </div>
        <MessageSquare className="w-8 h-8 text-orange-200" />
      </div>
    </div>
  </div>
</div>

           {/* Search and Filters */}
           <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-8">
  {/* Barre de recherche normale */}
  <div className="flex flex-col lg:flex-row gap-4 items-center justify-between mb-4">
    <div className="flex-1 relative">
      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
      {filterLoading && (
        <Loader2 className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-500 animate-spin" />
      )}
      <input
        type="text"
        placeholder="Search by agent email, task ID, auditor name, issue names, or comments..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
      />
    </div>
    
    <div className="flex gap-3">
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all"
      >
        <Filter className="w-5 h-5" />
        Filters
        <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
      </button>
      
      <select
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value)}
        className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="audited_at">Sort by Audit Date</option>
        <option value="task_date">Sort by Task Date</option>
        <option value="agent_email">Sort by Agent Email</option>
        <option value="match_task_id">Sort by Task ID</option>
      </select>
      
      <button
        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
        className="px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all"
      >
        {sortOrder === 'asc' ? <SortAsc className="w-5 h-5" /> : <SortDesc className="w-5 h-5" />}
      </button>
    </div>
  </div>

  {/* Advanced Filters */}
  {showFilters && (
    <div className="border-t border-gray-200 pt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
        <select
          value={filters.dateType}
          onChange={(e) => handleDateTypeChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="today">Today</option>
          <option value="week">Last 7 days</option>
          <option value="month">Current Month</option>
          <option value="custom">Custom Range</option>
        </select>
      </div>
      
      {filters.dateType === 'custom' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </>
      )}
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Auditor</label>
        <select
          value={filters.auditor}
          onChange={(e) => setFilters(prev => ({ ...prev, auditor: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">All Auditors</option>
          {auditors.map(auditor => (
            <option key={auditor.$id} value={auditor.$id}>
              {auditor.full_name} ({auditor.email})
            </option>
          ))}
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Agent Email</label>
        <input
          type="text"
          placeholder="Filter by agent email"
          value={filters.agentEmail}
          onChange={(e) => setFilters(prev => ({ ...prev, agentEmail: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Task Type</label>
        <select
          value={filters.taskType}
          onChange={(e) => setFilters(prev => ({ ...prev, taskType: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">All Types</option>
          <option value="pair_only">Pair Only</option>
          <option value="complete_match">Complete Match</option>
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Audit Status</label>
        <select
          value={filters.auditStatus}
          onChange={(e) => setFilters(prev => ({ ...prev, auditStatus: e.target.value }))}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
        >
          <option value="">All Status</option>
          <option value="approved">✅ Approved</option>
          <option value="rejected">💬 Commented</option>
        </select>
      </div>
    </div>
  )}
</div>

{/* ✅ BARRE DE FILTRES FIXE AU SCROLL */}
<div className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200 z-40 transform -translate-y-full transition-transform duration-300" id="sticky-filters">
  <div className="max-w-7xl mx-auto px-6 py-3">
    <div className="flex items-center gap-4">
      {/* Recherche compacte */}
      <div className="flex-1 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search comments, emails, IDs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
        />
      </div>

      {/* Filtres rapides */}
      <select
  value={filters.dateType}
  onChange={(e) => handleDateTypeChange(e.target.value)}
  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
>
  <option value="today">Today</option>
  <option value="week">Last 7 days</option>
  <option value="month">Current Month</option>
  <option value="custom">Custom Range</option>
</select>

{/* Dates custom dans la barre fixe */}
{filters.dateType === 'custom' && (
  <>
    <input
      type="date"
      value={filters.dateFrom}
      onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
    />
    <input
      type="date"
      value={filters.dateTo}
      onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
    />
  </>
)}

    <select
      value={filters.auditStatus}
      onChange={(e) => setFilters(prev => ({ ...prev, auditStatus: e.target.value }))}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    >
      <option value="">All Status</option>
      <option value="approved">Approved</option>
      <option value="rejected">Commented</option>
    </select>

    <select
      value={filters.taskType}
      onChange={(e) => setFilters(prev => ({ ...prev, taskType: e.target.value }))}
      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
    >
      <option value="">All Types</option>
      <option value="pair_only">Pair Only</option>
      <option value="complete_match">Complete Match</option>
    </select>

    <select
      value={filters.auditor}
      onChange={(e) => setFilters(prev => ({ ...prev, auditor: e.target.value }))}
      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
    >
      <option value="">All Auditors</option>
      {auditors.map(auditor => (
        <option key={auditor.$id} value={auditor.$id}>{auditor.full_name}</option>
      ))}
    </select>

      {/* Bouton retour en haut */}
      <button
        onClick={handleRefreshData}
        disabled={filterLoading}
        className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium flex items-center gap-1 disabled:opacity-50"
      >
        {filterLoading ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <RefreshCw className="w-3 h-3" />
        )}
        Refresh
      </button>

      {/* Bouton retour en haut */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
      >
        ↑ Top
      </button>
    </div>
  </div>
</div>
         </div>
       </div>

       {/* Results */}
       <div className="max-w-7xl mx-auto px-6 py-8">
       <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            {filterLoading && <Loader2 className="w-6 h-6 animate-spin text-blue-500" />}
            {displayStats.displayed.toLocaleString()} Result{displayStats.displayed !== 1 ? 's' : ''} Displayed
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Out of {globalStats.total.toLocaleString()} total audit records matching your filters
          </p>
        </div>
        <div className="text-sm text-gray-600">
          <div>Period: {new Date(filters.dateFrom).toLocaleDateString()} to {new Date(filters.dateTo).toLocaleDateString()}</div>
          {hasMore && (
            <div className="text-blue-600 font-medium mt-1">
              More results available - scroll down to load
            </div>
          )}
        </div>
      </div>

         {filteredResults.length === 0 ? (
           <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
             <AlertTriangle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
             <h3 className="text-xl font-semibold text-gray-800 mb-2">No Results Found</h3>
             <p className="text-gray-600">Try adjusting your filters or search criteria</p>
           </div>
         ) : (
           <div className={viewMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 gap-8' : 'space-y-6'}>
             {filteredResults.map((result) => {
               const imageUrl = result.task_type === 'complete_match' 
               ? result.template_image_url 
               : (result.agent_submitted_url || result.model_template_url);
               const imageToUse = imageUrl;
               
               return (
                 <div
                   key={result.$id}
                   onClick={() => setSelectedResult(result)}
                   className={`bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group ${
                     viewMode === 'list' ? 'flex items-center p-6' : 'p-6'
                   }`}
                 >
                   {/* Image */}
                   <div className={viewMode === 'list' ? 'flex-shrink-0 mr-6' : 'mb-6'}>
                   <div className={`relative rounded-xl overflow-hidden ${viewMode === 'list' ? 'w-40 h-28' : 'w-full h-80'} bg-gray-100 group-hover:scale-105 transition-transform`}>
                        {imageUrl ? (
                        <ImageWithFallback
                        src={imageUrl}
                        fallbackSrc={
                            result.task_type === 'complete_match' 
                              ? undefined // Pas de fallback pour complete_match, juste template_image_url
                              : (result.model_template_url !== imageUrl ? result.model_template_url : result.agent_submitted_url)
                          }
                            alt="Task Image"
                            className="w-full h-full object-cover"
                            onClick={() => setSelectedResult(result)}
                        />
                        ) : (
                        <div 
                            className="w-full h-full flex items-center justify-center text-gray-400 cursor-pointer"
                            onClick={() => setSelectedResult(result)}
                        >
                            <div className="text-center">
                            <Image className="w-8 h-8 mx-auto mb-2" />
                            <p className="text-xs">No image available</p>
                            </div>
                        </div>
                        )}
                        
                        {/* Status Badge */}
                        <div className="absolute top-3 right-3">
                        {result.audit_status === 'approved' ? (
                            <div className="flex items-center gap-1 bg-green-500 text-white px-3 py-1 rounded-lg text-sm font-semibold shadow-lg">
                            <CheckCircle className="w-4 h-4" />
                            Approved
                            </div>
                        ) : (
                            <div className="flex items-center gap-1 bg-red-500 text-white px-3 py-1 rounded-lg text-sm font-semibold shadow-lg">
                            <XCircle className="w-4 h-4" />
                            Rejected
                            </div>
                        )}
                        </div>

                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="bg-white/90 px-4 py-2 rounded-lg text-gray-800 font-semibold">
                            Click to view details
                        </div>
                        </div>
                    </div>
                    </div>

                   {/* Content */}
                   <div className="flex-1 space-y-4">
                     {/* Task Header */}
                     <div className="flex items-center justify-between">
                       <div className="flex items-center gap-2">
                         <Hash className="w-5 h-5 text-blue-500" />
                         <span className="text-xl font-bold text-gray-800">#{result.match_task_id}</span>
                       </div>
                       <div className="flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-lg text-sm font-medium">
                         <Tag className="w-4 h-4" />
                         {result.task_type.replace('_', ' ').toUpperCase()}
                       </div>
                     </div>

                     {/* Main Info */}
                     <div className="space-y-3">
                       <div className="flex items-center gap-2">
                         <Mail className="w-4 h-4 text-gray-500" />
                         <span className="text-sm font-medium text-gray-700">{result.agent_email}</span>
                       </div>

                       <div className="flex items-center gap-2">
                         <User className="w-4 h-4 text-purple-500" />
                         <span className="text-sm text-gray-600">Audited by {result.auditor_name}</span>
                       </div>

                       {/* ✅ Issue Names Display */}
                       <div className="flex items-center gap-2">
                         <AlertTriangle className="w-4 h-4 text-orange-500" />
                         {result.issue_names && result.issue_names.trim() ? (
                           <span className="text-sm font-medium text-gray-700">{result.issue_names}</span>
                         ) : (
                           <span className="text-sm text-gray-500 italic">No issue labels</span>
                         )}
                       </div>
                     </div>

                     {/* ✅ COMMENT EN AVANT - Plus visible */}
                     {result.audit_comment && result.audit_comment.trim() !== '' ? (
                       <div className={`${result.audit_status === 'rejected' ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300'} border-2 rounded-xl p-4`}>
                         <div className="flex items-start gap-2">
                           <MessageSquare className={`w-5 h-5 ${result.audit_status === 'rejected' ? 'text-red-600' : 'text-yellow-600'} mt-0.5 flex-shrink-0`} />
                           <div className="flex-1">
                             <p className={`text-xs font-bold ${result.audit_status === 'rejected' ? 'text-red-800' : 'text-yellow-800'} mb-1 uppercase tracking-wide`}>
                               🚨 AUDIT FEEDBACK
                             </p>
                             <p className={`text-sm ${result.audit_status === 'rejected' ? 'text-red-800' : 'text-yellow-800'} font-medium leading-relaxed`}>
                               {result.audit_comment}
                             </p>
                           </div>
                         </div>
                       </div>
                     ) : (
                       <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                         <div className="flex items-center gap-2">
                           <CheckCircle className="w-5 h-5 text-green-600" />
                           <div>
                             <p className="text-xs font-bold text-green-800 uppercase tracking-wide">✅ NO ISSUES</p>
                             <p className="text-sm text-green-700 font-medium">Task passed without comments</p>
                           </div>
                         </div>
                       </div>
                     )}

                     {/* Timeline */}
                     <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-xs text-gray-600">
                       <div className="flex items-center gap-2">
                         <Clock className="w-3 h-3" />
                         <span>Task: {formatDate(result.task_date)}</span>
                       </div>
                       <div className="flex items-center gap-2">
                         <Star className="w-3 h-3" />
                         <span>Audited: {formatDate(result.audited_at)}</span>
                       </div>
                     </div>
                   </div>
                 </div>
               );
             })}
           </div>
         )}

         {/* Load More Button */}
         {filteredResults.length > 0 && hasMore && (
            <div className="text-center mt-12">
                <button 
                onClick={loadMoreResults} // ✅ Fonction sans paramètres
                disabled={filterLoading}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                >
                {filterLoading ? (
                    <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading more...
                    </>
                ) : (
                    <>
                    <RefreshCw className="w-5 h-5" />
                    Load More Results
                    </>
                )}
                </button>
                <p className="text-sm text-gray-500 mt-2">
                Showing {filteredResults.length} results
                </p>
            </div>
            )}
       </div>

       {/* Footer */}
       <div className="bg-white border-t border-gray-200 py-8">
         <div className="max-w-7xl mx-auto px-6 text-center">
           <p className="text-gray-600">
             © 2025 - Quality Assurance Dashboard
           </p>
         </div>
       </div>
     </div>

     {/* Modal */}
     <AuditModal result={selectedResult} onClose={() => setSelectedResult(null)} />
   </>
 );
};

export default AuditResultsInterface;